import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Globe2, Images, ArrowRight, ExternalLink } from 'lucide-react';
import PageHero from '../components/PageHero';
import Section from '../components/Section';
import { SF_UI } from '../components/ui/tokens';
import SfutLegacyShowcase from '../components/SfutLegacyShowcase';
import { SFUT_LEGACY_GALLERY_URL, SFUT_LEGACY_MINT_URL } from '../data/sfutLegacy';

const DOORS = [
  {
    id: 'classics',
    title: 'Classics',
    body: 'Olympic Series on Apertum — wallet path, contract, and marketplace links.',
    to: '/legacy/classics',
    cta: 'Open Classics',
    external: false,
    icon: Trophy,
  },
  {
    id: 'one-world',
    title: 'One World',
    body: 'Sport worlds in the SportsFuture ecosystem. OneTaekwondo is first; more open next.',
    to: '/legacy/one-world',
    cta: 'Open One World',
    external: false,
    icon: Globe2,
  },
  {
    id: 'sfut-legacy',
    title: 'SFUT Legacy',
    body: 'Athlete highlight NFTs on OpenPlaza. Browse the gallery there — no wallet on this page.',
    to: SFUT_LEGACY_GALLERY_URL,
    cta: 'Open SFUT gallery',
    external: true,
    icon: Images,
  },
] as const;

const LegacyHubPage = () => {
  return (
    <>
      <PageHero
        eyebrow="Legacy"
        title="Legacy"
        subtitle="Classics, One World, and SFUT Legacy highlights. The gallery lives on OpenPlaza."
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-3">
          {DOORS.map((door, index) => {
            const Icon = door.icon;
            const ctaClass = `${SF_UI.systemId} sf-cta mt-auto inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary/20 transition-all hover:bg-primary-600 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50`;
            return (
              <motion.article
                key={door.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className={`${SF_UI.surfaceCard} ${SF_UI.surfaceCardPad} flex flex-col border border-gray-100`}
                data-testid={`legacy-hub-${door.id}`}
              >
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary">
                    <Icon size={32} aria-hidden />
                  </div>
                </div>
                <h2 className="mb-3 text-center text-xl font-semibold">{door.title}</h2>
                <p className="mb-6 flex-1 text-center text-gray-600">{door.body}</p>
                {door.external ? (
                  <a
                    href={door.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={ctaClass}
                    data-testid="legacy-hub-sfut-gallery"
                  >
                    {door.cta}
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                ) : (
                  <Link to={door.to} className={ctaClass}>
                    {door.cta}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                )}
              </motion.article>
            );
          })}
        </div>
      </Section>
      <Section bgColor="bg-gradient-to-br from-slate-800 to-blue-900" className="text-white pt-12">
        <div className="mb-10 text-center">
          <h2 className="mb-3 text-3xl font-bold text-white">SFUT Legacy Gallery</h2>
          <p className="mb-6 text-white/90">
            Five random highlights. Full collection on OpenPlaza.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={SFUT_LEGACY_GALLERY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-medium text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              data-testid="legacy-hub-open-gallery"
            >
              Open SFUT Legacy gallery
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={SFUT_LEGACY_MINT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-white/40 bg-white/10 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              data-testid="legacy-hub-mint"
            >
              Mint
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </div>
        </div>
        <SfutLegacyShowcase maxItems={5} compact showCta={false} />
      </Section>
    </>
  );
};

export default LegacyHubPage;
